import { proximaTela } from "./utils/nextPath";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, Link } from "react-router-dom";
import { PausesEnabledGuard } from "./utils/pausesEnabled";
import { Suspense, useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Loader2 } from "lucide-react";
import { accentText } from "./utils/accent";
import {
  caminhoForaDoPlano,
  carregarCatalogoPermissoes,
  chaveDaRota,
  podeAgir,
  rotuloDaChave,
} from "./utils/permissions";
import { lazyWithRetry as lazy } from "./utils/lazyWithRetry";
import { startAdminWS, stopAdminWS } from "./lib/adminWS";
import TelemetryAlertsListener from "./components/TelemetryAlertsListener";
import ImpersonationBanner from "./components/admin/ImpersonationBanner";

// ╔════════════════════════════════════════════════════════════╗
// ║ Code splitting (Fase 3) — React.lazy + Suspense por rota   ║
// ║                                                            ║
// ║ Antes: bundle único de ~2.3 MB carregado no primeiro paint ║
// ║ Depois: cada rota vira um chunk separado carregado on-demand║
// ║ → time-to-interactive muito menor, especialmente no mobile ║
// ║                                                            ║
// ║ LoginPage NÃO é lazy — é a primeira tela e precisa estar   ║
// ║ disponível imediatamente (UX crítica).                     ║
// ╚════════════════════════════════════════════════════════════╝
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AdminLayout from "./layouts/AdminLayout";
import GlobalToaster from "./components/notifications/GlobalToaster";
import ErrorBoundary from "./components/ErrorBoundary";
import { CommunicationsProvider } from "./components/communications/CommunicationsProvider";
import CommunicationOverlays from "./components/communications/CommunicationOverlays";
import HoverTip from "./components/HoverTip";
import { TooltipProvider } from "./components/ui/tooltip";

const HomeDashboardPage = lazy(() => import("./pages/HomeDashboardPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const TenantAuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const AccessLivePage = lazy(() => import("./pages/audit/AccessLivePage"));
const ClosingPage = lazy(() => import("./pages/services/ClosingPage"));
const SessionsPage = lazy(() => import("./pages/audit/SessionsPage"));
const SecurityAlertsPage = lazy(() => import("./pages/audit/SecurityAlertsPage"));
const GeneralSettingsPage = lazy(() => import("./pages/GeneralSettingsPage"));
const FleetMaintenancePage = lazy(() => import("./pages/fleet/FleetMaintenancePage"));
const MaintenancePlansPage = lazy(() => import("./pages/fleet/MaintenancePlansPage"));
const FleetFinesPage = lazy(() => import("./pages/fines/FleetFinesPage"));
const FleetIpvaPage = lazy(() => import("./pages/ipva/FleetIpvaPage"));
const ClimatizationOverviewPage = lazy(() => import("./pages/climatization/ClimatizationOverviewPage"));
const ClimatizationInstallationsPage = lazy(() => import("./pages/climatization/InstallationsPage"));
const ClimatizationSchedulePage = lazy(() => import("./pages/climatization/SchedulePage"));
const ClimatizationEquipmentQrPage = lazy(() => import("./pages/climatization/EquipmentQrPage"));
const ClimatizationRequestsPage = lazy(() => import("./pages/climatization/ServiceRequestsPage"));
const PublicClimatizationRequestPage = lazy(() => import("./pages/PublicClimatizationRequestPage"));
const PublicRequestTrackingPage = lazy(() => import("./pages/PublicRequestTrackingPage"));
const PublicPmocValidationPage = lazy(() => import("./pages/PublicPmocValidationPage"));
const AccessProfilesPage = lazy(() => import("./pages/AccessProfilesPage"));
const FuelPage = lazy(() => import("./pages/fleet/FuelPage"));
const FuelTypesPage = lazy(() => import("./pages/FuelTypesPage"));
const CancelReasonsPage = lazy(() => import("./pages/CancelReasonsPage"));
const QuickMessagesPage = lazy(() => import("./pages/QuickMessagesPage"));
const RejectionReasonsPage = lazy(() => import("./pages/RejectionReasonsPage"));
const PausesGeneralPage = lazy(() => import("./pages/PausesGeneralPage"));
const PausesReasonsPage = lazy(() => import("./pages/PausesReasonsPage"));
const PausesHistoryPage = lazy(() => import("./pages/PausesHistoryPage"));
const ProductivityPage = lazy(() => import("./pages/ProductivityPage"));
const UserProductivityPage = lazy(() => import("./pages/reports/UserProductivityPage"));
const AIReportsPage = lazy(() => import("./pages/AIReportsPage"));
const ServicesReadyReport = lazy(() => import("./pages/reports/ready/ServicesReadyReport"));
const SlaReadyReport = lazy(() => import("./pages/reports/ready/SlaReadyReport"));
const BillingReadyReport = lazy(() => import("./pages/reports/ready/BillingReadyReport"));
const InventoryReadyReport = lazy(() => import("./pages/reports/ready/InventoryReadyReport"));
const PortalsReadyReport = lazy(() => import("./pages/reports/ready/PortalsReadyReport"));
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"));
const BrandingPage = lazy(() => import("./pages/BrandingPage"));
const PointsOfInterestPage = lazy(() => import("./pages/PointsOfInterestPage"));
const PeoplePage = lazy(() => import("./pages/PeoplePage"));
const ChecklistsPage = lazy(() => import("./pages/ChecklistsPage"));
const TagsPage = lazy(() => import("./pages/TagsPage"));
const BranchesPage = lazy(() => import("./pages/BranchesPage"));
const ServiceTypesPage = lazy(() => import("./pages/ServiceTypesPage"));
const ReportsListPage = lazy(() => import("./pages/reports/ReportsListPage"));
const DashboardsListPage = lazy(() => import("./pages/dashboard/DashboardsListPage"));
const DashboardEditorPage = lazy(() => import("./pages/dashboard/DashboardEditorPage"));
const ReportBuilderPage = lazy(() => import("./pages/reports/ReportBuilderPage"));
const CustomFieldsPage = lazy(() => import("./pages/CustomFieldsPage"));
const FinanceCategoriesPage = lazy(() => import("./pages/FinanceCategoriesPage"));
const FinanceGeneralSettingsPage = lazy(() => import("./pages/finance/FinanceGeneralSettingsPage"));
const BankAccountsPage = lazy(() => import("./pages/finance/BankAccountsPage"));
const PaymentMethodsPage = lazy(() => import("./pages/finance/PaymentMethodsPage"));
const CostCentersPage = lazy(() => import("./pages/finance/CostCentersPage"));
const FinanceEntriesPage = lazy(() => import("./pages/finance/FinanceEntriesPage"));
const FinanceStatementPage = lazy(() => import("./pages/finance/FinanceStatementPage"));
const OfxImportsHistoryPage = lazy(() => import("./pages/finance/OfxImportsHistoryPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const LogisticsPanelPage = lazy(() => import("./pages/LogisticsPanelPage"));
const InventoryPage = lazy(() => import("./pages/inventory/InventoryPage"));
const InsurancePoliciesPage = lazy(() => import("./pages/insurance/InsurancePoliciesPage"));
const InsuranceClaimsPage = lazy(() => import("./pages/insurance/InsuranceClaimsPage"));
const WallboardPage = lazy(() => import("./pages/WallboardPage"));
const ModelCockpitPage = lazy(() => import("./pages/models/ModelCockpitPage"));
const ModelWorkbenchPage = lazy(() => import("./pages/models/ModelWorkbenchPage"));
const ModelPulsePage = lazy(() => import("./pages/models/ModelPulsePage"));
const ModelCommandDockPage = lazy(() => import("./pages/models/ModelCommandDockPage"));
const ModelCanvasFlowPage = lazy(() => import("./pages/models/ModelCanvasFlowPage"));
const ModelRadarMatrixPage = lazy(() => import("./pages/models/ModelRadarMatrixPage"));
// eslint-disable-next-line no-unused-vars -- Fechamento antigo oculto por ora; não excluir.
const AuditsPage = lazy(() => import("./pages/AuditsPage"));
const FaturamentoPage = lazy(() => import("./pages/billing/BillingPage"));
const FaturamentoClientPage = lazy(() => import("./pages/billing/BillingClientPage"));
const FiscalOverviewPage = lazy(() => import("./pages/fiscal/FiscalOverviewPage"));
const FiscalProfilesPage = lazy(() => import("./pages/fiscal/FiscalProfilesPage"));
const NFSePage = lazy(() => import("./pages/fiscal/NFSePage"));
const CommissionsPage = lazy(() => import("./pages/CommissionsPage"));
const RatingsPage = lazy(() => import("./pages/ratings/RatingsPage"));
const CaptureHistoryPage = lazy(() => import("./pages/capture/CaptureHistoryPage"));
const RatingsSettingsPage = lazy(() => import("./pages/settings/RatingsSettingsPage"));
const SpeedControlPage = lazy(() => import("./pages/SpeedControlPage"));
const MobileViewSettingsPage = lazy(() => import("./pages/settings/MobileViewSettingsPage"));
const DispatchExpirationPage = lazy(() => import("./pages/DispatchExpirationPage"));
const WhatsNewPage = lazy(() => import("./pages/WhatsNewPage"));
const NotificationSettingsPage = lazy(() => import("./pages/NotificationSettingsPage"));
const DispatchRoutingPage = lazy(() => import("./pages/DispatchRoutingPage"));
const TrackersPage = lazy(() => import("./pages/TrackersPage"));
const AutomationsPage = lazy(() => import("./pages/automations/AutomationsPage"));
const PortalsPage = lazy(() => import("./pages/portals/PortalsPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const TenantsListPage = lazy(() => import("./pages/admin/TenantsListPage"));
const TenantDetailPage = lazy(() => import("./pages/admin/TenantDetailPage"));
const PlansPage = lazy(() => import("./pages/admin/PlansPage"));
const BillingPage = lazy(() => import("./pages/admin/BillingPage"));
const AuditLogPage = lazy(() => import("./pages/admin/AuditLogPage"));
const AdminTeamPage = lazy(() => import("./pages/admin/AdminTeamPage"));
const PortfoliosPage = lazy(() => import("./pages/admin/PortfoliosPage"));
const TenantTagsPage = lazy(() => import("./pages/admin/TenantTagsPage"));
const AdminBanksPage = lazy(() => import("./pages/admin/BanksPage"));
const MyDayPage = lazy(() => import("./pages/admin/MyDayPage"));
const HelpVideosPage = lazy(() => import("./pages/admin/HelpVideosPage"));
const CommunicationsPage = lazy(() => import("./pages/admin/CommunicationsPage"));
const PositronConfigPage = lazy(() => import("./pages/admin/PositronConfigPage"));
const TelliConfigPage = lazy(() => import("./pages/admin/TelliConfigPage"));
const GlobalIntegrationsPage = lazy(() => import("./pages/admin/GlobalIntegrationsPage"));
const GoogleMapsUsagePage = lazy(() => import("./pages/admin/GoogleMapsUsagePage"));
const OpenAIUsagePage = lazy(() => import("./pages/admin/OpenAIUsagePage"));
const JevUsagePage = lazy(() => import("./pages/admin/JevUsagePage"));
const JevClassificationsPage = lazy(() => import("./pages/admin/JevClassificationsPage"));
const HereUsagePage = lazy(() => import("./pages/admin/HereUsagePage"));
const S3UsagePage = lazy(() => import("./pages/admin/S3UsagePage"));
const PlacasUsagePage = lazy(() => import("./pages/admin/PlacasUsagePage"));
const FocusUsagePage = lazy(() => import("./pages/admin/FocusUsagePage"));
const SmtpUsagePage = lazy(() => import("./pages/admin/SmtpUsagePage"));
const ServiceCatalogPage = lazy(() => import("./pages/admin/ServiceCatalogPage"));
const IpvaCalendarPage = lazy(() => import("./pages/admin/IpvaCalendarPage"));
const PortalServiceMapPage = lazy(() => import("./pages/admin/PortalServiceMapPage"));
const CapturesPage = lazy(() => import("./pages/admin/CapturesPage"));
const AppHealthPage = lazy(() => import("./pages/admin/AppHealthPage"));
const AdminFiscalPage = lazy(() => import("./pages/admin/AdminFiscalPage"));
const FrontendErrorsPage = lazy(() => import("./pages/admin/FrontendErrorsPage"));
const PublicReceiptPage = lazy(() => import("./pages/PublicReceiptPage"));
const ServicePrintPage = lazy(() => import("./pages/ServicePrintPage"));
const OnboardingCollectPage = lazy(() => import("./pages/public/OnboardingCollectPage"));
const PublicTrackingPage = lazy(() => import("./pages/PublicTrackingPage"));
const OnboardingPage = lazy(() => import("./pages/onboarding/OnboardingPage"));

// Fallback durante o lazy load — feedback visual mínimo
function RouteSuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className={`h-8 w-8 animate-spin ${accentText()}`} />
    </div>
  );
}

function WSBootstrap() {
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      // Na impersonation clássica o cookie de renovação é o do admin da
      // plataforma: renovar ali trocaria a identidade da sessão, então o WS
      // não renova sozinho nesse modo.
      startAdminWS({ podeRenovarSessao: user?.impersonation?.mode !== "user" });
      return () => stopAdminWS();
    }
    return undefined;
  }, [user]);
  return user ? <TelemetryAlertsListener /> : null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [catalogo, setCatalogo] = useState(null);

  useEffect(() => {
    if (user && !user.full_access) carregarCatalogoPermissoes().then(setCatalogo).catch(() => {});
  }, [user]);

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center bg-[#F1F5F9]">
        <Loader2 className="h-8 w-8 animate-spin" style={accentText()} />
      </div>
    );
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (user.must_change_password) return <Navigate to="/login" replace />;
  // Configuração inicial pendente: nenhuma tela interna do tenant abre antes de
  // o assistente ser concluído. A área /admin da plataforma não é bloqueada.
  if (
    user.onboarding_pendente &&
    !location.pathname.startsWith("/admin") &&
    location.pathname !== "/onboarding"
  )
    return <Navigate to="/onboarding" replace />;
  // Módulo que a empresa não contratou responde como página inexistente: dizer
  // "sem acesso" revelaria o que está à venda e sugeriria que alguém pode
  // liberar por dentro.
  if (caminhoForaDoPlano(user, location.pathname)) return <NaoEncontrado />;
  // Área da plataforma: quem é da equipe admin não tem perfil de acesso de
  // tenant — o que pode fazer vem de `admin_permissions` (ver AdminLayout).
  const equipePlataforma = ["super_admin", "support_admin", "billing_admin"].includes(
    user.role,
  );
  if (location.pathname.startsWith("/admin") && equipePlataforma) return children;
  if (!user.full_access) {
    if (!catalogo)
      return (
        <div className="h-screen flex items-center justify-center bg-[#F1F5F9]">
          <Loader2 className="h-8 w-8 animate-spin" style={accentText()} />
        </div>
      );
    const chave = chaveDaRota(catalogo, location.pathname);
    if (chave && !podeAgir(user, chave, "view"))
      return <SemAcesso motivo={rotuloDaChave(catalogo, chave)} />;
  }
  return children;
}

function NaoEncontrado() {
  return (
    <div
      className="h-screen flex flex-col items-center justify-center bg-[#F1F5F9] px-6 text-center"
      data-testid="pagina-nao-encontrada"
    >
      <h1 className="text-xl font-bold text-gray-900">Página não encontrada</h1>
      <p className="text-sm text-gray-500 mt-2 max-w-sm">
        O endereço que você abriu não existe nesta conta.
      </p>
      <a
        href="/dashboard"
        className="mt-5 h-9 px-4 inline-flex items-center rounded-md bg-[#00AFE5] text-white text-sm font-semibold"
      >
        Voltar ao início
      </a>
    </div>
  );
}

function SemAcesso({ motivo }) {
  const { user } = useAuth();
  const perfil = user?.access_profile_nome;
  return (
    <div
      className="h-screen flex flex-col items-center justify-center bg-[#F1F5F9] px-6 text-center"
      data-testid="sem-acesso"
    >
      <h1 className="text-xl font-bold text-gray-900">Sem acesso a esta área</h1>
      <p className="text-sm text-gray-500 mt-2 max-w-md" data-testid="sem-acesso-motivo">
        {motivo
          ? `Bloqueado: ${motivo} — ação 'visualizar' não liberada no seu perfil${perfil ? ` (perfil ${perfil})` : ""}.`
          : "Seu perfil de acesso não inclui esta tela."}
      </p>
      <p className="text-sm text-gray-500 mt-1 max-w-sm">
        Fale com o administrador da conta se precisar dessa permissão.
      </p>
      <Link to={user?.home_path || "/dashboard"} className="text-sm font-semibold mt-5" style={accentText()}>
        Voltar ao início
      </Link>
    </div>
  );
}

/** Para onde a pessoa vai ao entrar: equipe da plataforma começa no /admin. */
function paginaInicialDoUsuario(user) {
  if (user?.home_path) return user.home_path;
  if (!user?.client_id && ["support_admin", "billing_admin"].includes(user?.role))
    return "/admin";
  return "/dashboard";
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  // Lido uma vez: ao navegar a URL muda e o `next` some.
  const [destino] = useState(proximaTela);
  if (loading)
    return (
      <div className="h-screen flex items-center justify-center bg-[#F1F5F9]">
        <Loader2 className="h-8 w-8 animate-spin" style={accentText()} />
      </div>
    );
  // Senha provisória: fica na tela de login até definir a própria senha.
  if (user && !user.must_change_password)
    return <Navigate to={destino || paginaInicialDoUsuario(user)} replace />;
  return children;
}

function RotaInicial() {
  const { user } = useAuth();
  return <Navigate to={paginaInicialDoUsuario(user)} replace />;
}

function LegacyPanelRedirect() {
  const { id } = useParams();
  return <Navigate to={`/panels/${id}`} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/receipt/:token" element={<PublicReceiptPage />} />
      <Route path="/coleta-documentos/:token" element={<OnboardingCollectPage />} />
      <Route path="/acompanhar/:token" element={<PublicTrackingPage />} />
      <Route path="/qr/equipment/:token" element={<PublicClimatizationRequestPage kind="equipment" />} />
      <Route
        path="/public/climatizacao/:token"
        element={<PublicClimatizationRequestPage kind="installation" />}
      />
      <Route path="/public/solicitacoes/:token" element={<PublicRequestTrackingPage />} />
      <Route path="/pmoc/validar/:token" element={<PublicPmocValidationPage />} />
      {/* Compat: rota antiga em PT, mantida pra não quebrar links que
          já foram compartilhados via WhatsApp antes da renomeação. */}
      <Route path="/recibo/:token" element={<PublicReceiptPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <HomeDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/profiles"
        element={
          <ProtectedRoute>
            <AccessProfilesPage />
          </ProtectedRoute>
        }
      />
      <Route path="/settings/logs" element={<Navigate to="/audit/logs" replace />} />
      <Route
        path="/audit/logs"
        element={
          <ProtectedRoute>
            <TenantAuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services/closing"
        element={
          <ProtectedRoute>
            <ClosingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit/access"
        element={
          <ProtectedRoute>
            <AccessLivePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit/sessions"
        element={
          <ProtectedRoute>
            <SessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit/alerts"
        element={
          <ProtectedRoute>
            <SecurityAlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/general"
        element={
          <ProtectedRoute>
            <GeneralSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/config/general" element={<Navigate to="/settings/general" replace />} />
      <Route
        path="/fleet/maintenance"
        element={
          <ProtectedRoute>
            <FleetMaintenancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/climatization"
        element={
          <ProtectedRoute>
            <ClimatizationOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/climatization/installations"
        element={
          <ProtectedRoute>
            <ClimatizationInstallationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/climatization/schedule"
        element={
          <ProtectedRoute>
            <ClimatizationSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/climatization/requests"
        element={
          <ProtectedRoute>
            <ClimatizationRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/climatization/equipment-qr/:equipmentId"
        element={
          <ProtectedRoute>
            <ClimatizationEquipmentQrPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/fuel"
        element={
          <ProtectedRoute>
            <FuelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/maintenance/plans"
        element={
          <ProtectedRoute>
            <MaintenancePlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/fines"
        element={
          <ProtectedRoute>
            <FleetFinesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fleet/ipva"
        element={
          <ProtectedRoute>
            <FleetIpvaPage />
          </ProtectedRoute>
        }
      />
      <Route path="/config" element={<Navigate to="/settings/general" replace />} />
      <Route
        path="/settings/fuel-types"
        element={
          <ProtectedRoute>
            <FuelTypesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/cancel-reasons"
        element={
          <ProtectedRoute>
            <CancelReasonsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/rejection-reasons"
        element={
          <ProtectedRoute>
            <RejectionReasonsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/pauses"
        element={<Navigate to="/settings/pauses/general" replace />}
      />
      <Route
        path="/settings/pauses/general"
        element={
          <ProtectedRoute>
            <PausesGeneralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/pauses/reasons"
        element={
          <ProtectedRoute>
            <PausesEnabledGuard>
              <PausesReasonsPage />
            </PausesEnabledGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/pauses/history"
        element={
          <ProtectedRoute>
            <PausesEnabledGuard>
              <PausesHistoryPage />
            </PausesEnabledGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/productivity"
        element={
          <ProtectedRoute>
            <ProductivityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/productivity/users"
        element={
          <ProtectedRoute>
            <UserProductivityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/services"
        element={
          <ProtectedRoute>
            <ServicesReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/services/:aba"
        element={
          <ProtectedRoute>
            <ServicesReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/sla"
        element={
          <ProtectedRoute>
            <SlaReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/sla/:aba"
        element={
          <ProtectedRoute>
            <SlaReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/billing"
        element={
          <ProtectedRoute>
            <BillingReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/billing/:aba"
        element={
          <ProtectedRoute>
            <BillingReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/inventory"
        element={
          <ProtectedRoute>
            <InventoryReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/inventory/:aba"
        element={
          <ProtectedRoute>
            <InventoryReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/portals"
        element={
          <ProtectedRoute>
            <PortalsReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/ready/portals/:aba"
        element={
          <ProtectedRoute>
            <PortalsReadyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/new"
        element={
          <ProtectedRoute>
            <ReportBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <ProtectedRoute>
            <ReportBuilderPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/panels"
        element={
          <ProtectedRoute>
            <DashboardsListPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/panels/:id"
        element={
          <ProtectedRoute>
            <DashboardEditorPage />
          </ProtectedRoute>
        }
      />

      <Route path="/dashboards" element={<Navigate to="/panels" replace />} />
      <Route path="/dashboards/:id" element={<LegacyPanelRedirect />} />

      <Route
        path="/ai-reports"
        element={
          <ProtectedRoute>
            <AIReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/quick-messages"
        element={
          <ProtectedRoute>
            <QuickMessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/vehicles"
        element={
          <ProtectedRoute>
            <VehiclesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/speed-control"
        element={
          <ProtectedRoute>
            <SpeedControlPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/dispatch-expiration"
        element={
          <ProtectedRoute>
            <DispatchExpirationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new"
        element={
          <ProtectedRoute>
            <WhatsNewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/dispatch-routing"
        element={
          <ProtectedRoute>
            <DispatchRoutingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/trackers"
        element={
          <ProtectedRoute>
            <TrackersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/automations"
        element={
          <ProtectedRoute>
            <AutomationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/branding"
        element={
          <ProtectedRoute>
            <BrandingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/pois"
        element={
          <ProtectedRoute>
            <PointsOfInterestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/people"
        element={
          <ProtectedRoute>
            <PeoplePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/mobile/view"
        element={
          <ProtectedRoute>
            <MobileViewSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/checklists"
        element={
          <ProtectedRoute>
            <ChecklistsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/tags"
        element={
          <ProtectedRoute>
            <TagsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/branches"
        element={
          <ProtectedRoute>
            <BranchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/service-types"
        element={
          <ProtectedRoute>
            <ServiceTypesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/custom-fields"
        element={
          <ProtectedRoute>
            <CustomFieldsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services/list"
        element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      {/* Impressão interna do Serviço — documento igual ao recibo público,
          com os blocos escolhidos no modal "Impressão" do drawer. */}
      <Route
        path="/servicos/:id/imprimir"
        element={
          <ProtectedRoute>
            <ServicePrintPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/finance/general"
        element={
          <ProtectedRoute>
            <FinanceGeneralSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/finance/categories"
        element={
          <ProtectedRoute>
            <FinanceCategoriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/finance/bank-accounts"
        element={
          <ProtectedRoute>
            <BankAccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/finance/payment-methods"
        element={
          <ProtectedRoute>
            <PaymentMethodsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/finance/cost-centers"
        element={
          <ProtectedRoute>
            <CostCentersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/entries"
        element={
          <ProtectedRoute>
            <FinanceEntriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/statement"
        element={
          <ProtectedRoute>
            <FinanceStatementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/bank-accounts"
        element={<Navigate to="/settings/finance/bank-accounts" replace />}
      />
      <Route
        path="/finance/ofx-imports"
        element={
          <ProtectedRoute>
            <OfxImportsHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/:view"
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/policies"
        element={
          <ProtectedRoute>
            <InsurancePoliciesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insurance/claims"
        element={
          <ProtectedRoute>
            <InsuranceClaimsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portals"
        element={
          <ProtectedRoute>
            <PortalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portals/:view"
        element={
          <ProtectedRoute>
            <PortalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services/logistics"
        element={
          <ProtectedRoute>
            <LogisticsPanelPage />
          </ProtectedRoute>
        }
      />
      {/* Wallboard: aceita sessão normal OU token de tela (?token=), então a
          própria página resolve a autenticação em vez do ProtectedRoute. */}
      <Route path="/wallboard" element={<WallboardPage />} />
      {/* Modelos de design (mocks isolados, sem chamadas de API) */}
      <Route
        path="/models/cockpit"
        element={
          <ProtectedRoute>
            <ModelCockpitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/models/workbench"
        element={
          <ProtectedRoute>
            <ModelWorkbenchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/models/pulse"
        element={
          <ProtectedRoute>
            <ModelPulsePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/models/command-dock"
        element={
          <ProtectedRoute>
            <ModelCommandDockPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/models/canvas-flow"
        element={
          <ProtectedRoute>
            <ModelCanvasFlowPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/models/radar-matrix"
        element={
          <ProtectedRoute>
            <ModelRadarMatrixPage />
          </ProtectedRoute>
        }
      />
      {/* Fechamento antigo oculto por ora (página mantida em AuditsPage): redireciona ao novo. */}
      <Route path="/assessment" element={<Navigate to="/services/closing" replace />} />
      <Route path="/audits" element={<Navigate to="/services/closing" replace />} />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <FaturamentoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/client/:personId"
        element={
          <ProtectedRoute>
            <FaturamentoClientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fiscal"
        element={<ProtectedRoute><FiscalOverviewPage /></ProtectedRoute>}
      />
      <Route
        path="/fiscal/nfse"
        element={<ProtectedRoute><NFSePage /></ProtectedRoute>}
      />
      <Route
        path="/fiscal/profiles"
        element={<ProtectedRoute><FiscalProfilesPage /></ProtectedRoute>}
      />
      <Route
        path="/services/commissions"
        element={
          <ProtectedRoute>
            <CommissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services/ratings"
        element={<ProtectedRoute><RatingsPage modo="respostas" /></ProtectedRoute>}
      />
      <Route
        path="/services/ratings/indicators"
        element={<ProtectedRoute><RatingsPage modo="indicadores" /></ProtectedRoute>}
      />
      <Route
        path="/services/captures"
        element={<ProtectedRoute><CaptureHistoryPage /></ProtectedRoute>}
      />
      <Route
        path="/settings/ratings"
        element={<ProtectedRoute><RatingsSettingsPage /></ProtectedRoute>}
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      {/* Admin (super_admin) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="clients" element={<TenantsListPage />} />
        <Route path="clients/:id" element={<TenantDetailPage />} />
        <Route path="clients/:id/:tab" element={<TenantDetailPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="finance" element={<BillingPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="whats-new" element={<WhatsNewPage audiencia="admin" />} />
        <Route path="users" element={<AdminTeamPage />} />
        <Route path="portfolios" element={<PortfoliosPage />} />
        <Route path="tags" element={<TenantTagsPage />} />
        <Route path="banks" element={<AdminBanksPage />} />
        <Route path="my-day" element={<MyDayPage />} />
        <Route path="support-videos" element={<HelpVideosPage />} />
        <Route path="communications" element={<CommunicationsPage />} />
        <Route path="comunicacao" element={<Navigate to="/admin/communications" replace />} />
        <Route path="service-catalog" element={<ServiceCatalogPage />} />
        <Route path="ipva-calendar" element={<IpvaCalendarPage />} />
        <Route path="portal-service-map" element={<PortalServiceMapPage />} />
        <Route path="captures" element={<CapturesPage />} />
        <Route path="professionals" element={<AppHealthPage />} />
        <Route path="integrations" element={<GlobalIntegrationsPage />} />
        <Route path="integrations/google-maps" element={<GoogleMapsUsagePage />} />
        <Route path="integrations/openai" element={<OpenAIUsagePage />} />
        <Route path="integrations/jev" element={<JevUsagePage />} />
        <Route path="jev-classifications" element={<JevClassificationsPage />} />
        <Route path="integrations/here" element={<HereUsagePage />} />
        <Route path="integrations/s3" element={<S3UsagePage />} />
        <Route path="integrations/placas" element={<PlacasUsagePage />} />
        <Route path="integrations/focus-nfe" element={<FocusUsagePage />} />
        <Route path="integrations/smtp" element={<SmtpUsagePage />} />
        <Route path="integrations/invoices" element={<AdminFiscalPage />} />
        <Route path="integrations/positron" element={<PositronConfigPage />} />
        <Route path="integrations/telli" element={<TelliConfigPage />} />
        <Route path="erros" element={<FrontendErrorsPage />} />
      </Route>
      <Route path="*" element={<RotaInicial />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      {/* Provider global do Tooltip: badges com tooltip são usados em telas/modais
          variados e sem um provider na raiz eles derrubam a renderização. */}
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <AuthProvider>
            <CommunicationsProvider>
              <WSBootstrap />
              <GlobalToaster />
              <HoverTip />
              <ImpersonationBanner />
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
              <CommunicationOverlays />
            </CommunicationsProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
